import { useEffect, useRef, useState } from "react";
import API from "../api/axiosConfig";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  Share2,
  MapPin,
  BadgeCheck,
  Clock,
  Package,
  ChevronRight,
  Tag,
  Store,
  ImageOff,
  Star,
  MessageSquare,
  Eye,
} from "lucide-react";
import MobileBottomBar from "../components/MobileBottomBar";
import { toast } from "react-toastify";
import { isSedeOpen } from "../utils/sedeStatus";
import { getImageUrl } from "../utils/getImageUrl";
import { shareContent } from "../utils/share";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import CartQtyControl from "../components/CartQtyControl";

export default function CompanyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tabsRef = useRef(null);

  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [reviews,   setReviews]   = useState([]);
  const [reviewAvg, setReviewAvg] = useState(0);
  const [quickView, setQuickView] = useState(null); // { product, isOpen, closedMessage } | null

  // Load company first (accepts slug OR uuid), then use the real UUID for sub-requests
  useEffect(() => {
    API.get(`/companies/${id}`)
      .then((res) => {
        const c = res.data?.data?.company ?? res.data?.data ?? {};
        setCompany(c);
        const uuid = c.id;
        if (!uuid) return;

        API.get(`/products/company/public/${uuid}`)
          .then((r) => setProducts(r.data?.data?.products ?? []));

        API.get(`/secciones/company/${uuid}`)
          .then((r) => setSecciones(r.data?.data?.secciones ?? []));

        API.get(`/sedes/company/${uuid}`)
          .then((r) => setSedes(r.data?.data?.sedes ?? []))
          .catch(() => setSedes([]));

        API.get(`/reviews/public/company/${uuid}`)
          .then((r) => {
            setReviews(r.data?.data?.reviews ?? []);
            setReviewAvg(r.data?.data?.average ?? 0);
          })
          .catch(() => {});

        API.post(`/metrics/visit/${uuid}`).catch(() => {});
      })
      .catch(() => {});
  }, [id]);

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center gap-3">
        <div className="w-12 h-12 border-4 border-supernova-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-medium">Cargando empresa...</p>
      </div>
    );
  }

  /* ── Helpers ── */
  const getProductSedes = (product) => {
    if (Array.isArray(product.sedes)) return product.sedes;
    if (Array.isArray(product.productSedes))
      return product.productSedes.map((ps) => ps.sede ?? ps);
    return [];
  };

  const productHasOpenSede = (product) => {
    const s = getProductSedes(product);
    if (s.length === 0) return true;
    return s.some((x) => isSedeOpen(x).open);
  };

  const getClosedSedeMessage = (product) => {
    const s = getProductSedes(product);
    if (s.length === 0) return "Cerrado";
    return isSedeOpen(s[0]).message || "Cerrado";
  };

  const filteredProducts =
    activeTab === "all"
      ? products
      : products.filter((p) => {
          if (!p.seccion) return false;
          if (typeof p.seccion === "string") return p.seccion === activeTab;
          if (typeof p.seccion === "object")
            return (p.seccion.id ?? p.seccion._id) === activeTab;
          return false;
        });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aO = productHasOpenSede(a);
    const bO = productHasOpenSede(b);
    return aO === bO ? 0 : aO ? -1 : 1;
  });

  const openSedes = sedes.filter((s) => isSedeOpen(s).open);
  const hasOffers = products.some((p) => p.offerPrice);
  const categoryName = company.category
    ? typeof company.category === "object" ? company.category.name : company.category
    : null;

  const handleShare = async () => {
    const shareUrl = `${(process.env.REACT_APP_URL || "http://localhost:3000").replace(/\/$/, "")}/share/company/${company.id}`;
    try {
      const result = await shareContent({
        title: company.name,
        text: `Mira ${company.name} en Qkiere`,
        url: shareUrl,
        imageUrl: company.logo ? getImageUrl(company.logo) : null,
        fileName: "tienda.jpg",
      });
      if (result === "shared") toast.success("Compartido exitosamente");
      else if (result === "copied") toast.info("Enlace copiado al portapapeles");
    } catch {
      toast.error("No se pudo compartir");
    }
  };

  const scrollTabIntoView = (tabId) => {
    setActiveTab(tabId);
    if (tabsRef.current) {
      const btn = tabsRef.current.querySelector(`[data-tab="${tabId}"]`);
      btn?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-28 lg:pb-8">

      {/* ── Banner + Info card (unified) ─────────────────────── */}
      <div className="bg-white shadow-sm mb-2">
        {/* Banner */}
        <div className="relative overflow-hidden">
          <div
            className="h-28 sm:h-32 w-full"
            style={{ background: "linear-gradient(135deg, #ffe41b 0%, #fec400 50%, #e29800 100%)" }}
          >
            <div className="absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/4" />
          </div>

          <button
            onClick={() => navigate(-1)}
            className="absolute top-3 left-3 z-10 w-8 h-8 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/30 transition"
          >
            <ArrowLeft size={16} />
          </button>

          <button
            onClick={handleShare}
            className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/30 transition"
          >
            <Share2 size={14} />
          </button>
        </div>

        {/* Info row: logo left + details right */}
        <div className="px-3 sm:px-4 pt-3 pb-4">
          <div className="flex gap-3 items-start">
            {/* Logo */}
            <div className="w-[72px] h-[72px] flex-shrink-0 rounded-xl bg-white shadow-md border-2 border-white overflow-hidden flex items-center justify-center -mt-9 relative z-10">
              {company.logo ? (
                <img
                  src={getImageUrl(company.logo)}
                  alt={company.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 size={30} className="text-supernova-400" />
              )}
            </div>

            {/* Name + category + open status */}
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1 flex-wrap">
                    <h1 className="text-base font-bold text-gray-900 leading-tight">{company.name}</h1>
                    {company.verified && (
                      <BadgeCheck size={15} className="text-blue-500 flex-shrink-0" />
                    )}
                  </div>
                  {categoryName && (
                    <span className="text-xs text-supernova-700 font-semibold">{categoryName}</span>
                  )}
                </div>

                {/* Open/closed pill */}
                <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                  openSedes.length > 0
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-600"
                }`}>
                  {openSedes.length > 0 ? "Abierto" : "Cerrado"}
                </span>
              </div>

              {/* Description */}
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2">
                {company.description || "Esta empresa aún no tiene descripción."}
              </p>

              {/* Inline stats */}
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 flex-wrap">
                <span className="font-medium text-gray-700">{products.length} productos</span>
                {sedes.length > 0 && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="font-medium text-gray-700">{sedes.length} sede{sedes.length !== 1 ? "s" : ""}</span>
                  </>
                )}
                {hasOffers && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="flex items-center gap-0.5 text-supernova-600 font-semibold">
                      <Tag size={11} /> Tiene ofertas
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Sedes row — inside the card, separated by a thin border */}
          {sedes.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 mb-2">
                <Store size={13} className="text-gray-400" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ubicaciones</span>
              </div>
              <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
                <div className="flex gap-2 w-max pb-0.5">
                  {sedes.map((s) => {
                    const status = isSedeOpen(s);
                    return (
                      <button
                        key={s.id}
                        onClick={() => navigate(`/sede/${s.id}`)}
                        className="flex-shrink-0 text-left bg-gray-50 border border-gray-200 rounded-xl p-2.5 hover:border-supernova-300 hover:bg-supernova-50/40 transition w-40 sm:w-48 group"
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.open ? "bg-green-500" : "bg-red-400"}`} />
                          <span className={`text-xs font-semibold ${status.open ? "text-green-600" : "text-red-500"}`}>
                            {status.open ? "Abierto" : "Cerrado"}
                          </span>
                        </div>
                        <p className="text-xs font-semibold text-gray-800 line-clamp-1">{s.name}</p>
                        {s.address && (
                          <p className="text-xs text-gray-400 mt-0.5 flex items-start gap-0.5 line-clamp-1">
                            <MapPin size={9} className="flex-shrink-0 mt-0.5" />
                            {s.address}
                          </p>
                        )}
                        <div className="mt-1.5 flex items-center gap-0.5 text-supernova-600 text-xs font-semibold group-hover:gap-1 transition-all">
                          Ver <ChevronRight size={10} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Products section ─────────────────────────────────── */}
      <div className="bg-white shadow-sm">
        {/* Header + tabs */}
        <div className="px-3 sm:px-4 pt-3 pb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Package size={14} className="text-supernova-500" />
            Productos
          </h2>
          <span className="text-xs text-gray-400">
            {sortedProducts.length} resultado{sortedProducts.length !== 1 ? "s" : ""}
          </span>
        </div>

        {secciones.length > 0 && (
          <div ref={tabsRef} className="overflow-x-auto px-3 sm:px-4 pb-2 flex gap-1.5 scrollbar-hide">
            <button
              data-tab="all"
              onClick={() => scrollTabIntoView("all")}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition ${
                activeTab === "all"
                  ? "bg-supernova-500 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Todos
            </button>
            {secciones.map((s) => (
              <button
                key={s.id}
                data-tab={s.id}
                onClick={() => scrollTabIntoView(s.id)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition ${
                  activeTab === s.id
                    ? "bg-supernova-500 text-white shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {/* Products grid */}
        <div className="px-3 sm:px-4 pb-4">
          <AnimatePresence mode="wait">
            {sortedProducts.length > 0 ? (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5"
              >
                {sortedProducts.map((p, i) => {
                  const isOpen = productHasOpenSede(p);
                  const closedMsg = !isOpen ? getClosedSedeMessage(p) : null;
                  const hasOffer = p.offerPrice && Number(p.offerPrice) < Number(p.price);
                  const discount = hasOffer
                    ? Math.round((1 - Number(p.offerPrice) / Number(p.price)) * 100)
                    : 0;

                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => navigate(`/product/${p.id}`)}
                      className="relative bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                    >
                      <div className="relative w-full aspect-square bg-gray-100">
                        {p.images?.[0] ? (
                          <img
                            src={getImageUrl(p.images[0])}
                            alt={p.title}
                            className={`w-full h-full object-cover ${!isOpen ? "opacity-60" : ""}`}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                            <ImageOff size={20} className="text-gray-300" />
                            <span className="text-xs text-gray-300">Sin imagen</span>
                          </div>
                        )}

                        {hasOffer && (
                          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-md shadow">
                            -{discount}%
                          </div>
                        )}

                        {!isOpen && (
                          <div className="absolute inset-0 flex items-end justify-start p-1.5">
                            <span className="px-1.5 py-0.5 bg-gray-900/70 backdrop-blur-sm text-white text-xs font-semibold rounded-md flex items-center gap-1">
                              <Clock size={9} />
                              {closedMsg}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="p-2.5">
                        <h3 className="text-xs font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">
                          {p.title}
                        </h3>

                        {hasOffer ? (
                          <div>
                            <p className="text-sm font-bold text-supernova-600">
                              ${Number(p.offerPrice).toLocaleString("es-CO")}
                            </p>
                            <p className="text-xs text-gray-400 line-through">
                              ${Number(p.price).toLocaleString("es-CO")}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm font-bold text-supernova-600">
                            ${Number(p.price).toLocaleString("es-CO")}
                          </p>
                        )}

                        <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setQuickView({ product: p, isOpen, closedMessage: closedMsg })}
                            className="p-1.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition shrink-0"
                            title="Ver descripción"
                          >
                            <Eye size={12} className="text-gray-500" />
                          </button>
                          <CartQtyControl
                            product={p}
                            isOpen={isOpen}
                            size="sm"
                            addClassName="bg-supernova-500 hover:bg-supernova-600 text-white shadow-sm"
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-12 flex flex-col items-center gap-3"
              >
                <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
                  <Package size={24} className="text-gray-300" />
                </div>
                <p className="text-sm font-semibold text-gray-400">No hay productos en esta sección</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Reseñas del comercio ──────────────────────────────── */}
      {reviews.length > 0 && (
        <div className="mx-3 sm:mx-4 mb-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-gray-50">
            <div className="flex items-center gap-2">
              <MessageSquare size={15} className="text-supernova-500" />
              <h2 className="text-sm font-bold text-gray-800">Reseñas</h2>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-semibold">
                {reviews.length}
              </span>
            </div>
            {reviewAvg > 0 && (
              <div className="flex items-center gap-1">
                <Star size={13} className="text-yellow-400 fill-yellow-400" />
                <span className="text-sm font-bold text-gray-700">{reviewAvg}</span>
              </div>
            )}
          </div>

          <div className="divide-y divide-gray-50">
            {reviews.slice(0, 5).map((r) => (
              <div key={r.id} className="px-4 py-3">
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-supernova-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {r.user?.avatar
                      ? <img src={getImageUrl(r.user.avatar)} alt={r.user.name} className="w-full h-full object-cover" />
                      : <span className="text-xs font-bold text-supernova-600">{(r.user?.name ?? "?")[0].toUpperCase()}</span>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-gray-700 truncate">{r.user?.name ?? "Usuario"}</span>
                      <div className="flex gap-0.5 flex-shrink-0">
                        {[1,2,3,4,5].map((s) => (
                          <Star key={s} size={10} className={s <= r.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
                        ))}
                      </div>
                    </div>
                    {r.product?.title && (
                      <p className="text-[10px] text-gray-400 mb-0.5">{r.product.title}</p>
                    )}
                    {r.comment && (
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{r.comment}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <MobileBottomBar />

      {quickView && (
        <ProductQuickViewModal
          product={quickView.product}
          isOpen={quickView.isOpen}
          closedMessage={quickView.closedMessage}
          onClose={() => setQuickView(null)}
        />
      )}
    </div>
  );
}
